import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { label, formatDate } from "@/lib/constants";
import { CalendarClock, Save, ChevronRight, Wrench } from "lucide-react";

function Shell({ children }) {
  return (
    <div className="ti-shell -mx-5 -my-5 min-h-[calc(100vh-3.25rem)] px-5 py-6 sm:-mx-8 sm:-my-8 sm:px-8 sm:py-8">{children}</div>
  );
}

const META = {
  GREEN: { t: "text-emerald-300", b: "bg-emerald-500/15 text-emerald-300", dot: "bg-emerald-400", lbl: "On Track" },
  YELLOW: { t: "text-amber-300", b: "bg-amber-500/15 text-amber-300", dot: "bg-amber-400", lbl: "Due Soon" },
  RED: { t: "text-red-300", b: "bg-red-500/15 text-red-300", dot: "bg-red-400", lbl: "Overdue" },
  READY: { t: "text-sky-300", b: "bg-sky-500/15 text-sky-300", dot: "bg-sky-400", lbl: "Ready Delivery" },
  NO_TARGET: { t: "ti-muted", b: "bg-white/5 text-[#8A98B0]", dot: "bg-[#5B6B87]", lbl: "No Target" },
};
const today = () => new Date().toISOString().slice(0, 10);

function SummaryTile({ label: l, value, onClick, active, tone }) {
  return (
    <button data-testid={`prod-sum-${l.toLowerCase().replace(/\s/g, "-")}`} onClick={onClick} className={`ti-card ti-card-hover p-3 text-left ${active ? "border-[#3B82F6]" : ""}`}>
      <div className="text-[10px] font-semibold uppercase tracking-wider ti-muted">{l}</div>
      <div className={`mt-1 font-heading text-2xl font-bold ${tone || "text-white"}`}>{value ?? 0}</div>
    </button>
  );
}

export default function ProductionSchedule() {
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({});
  const [filter, setFilter] = useState("ALL");
  const [stage, setStage] = useState("");
  const [pic, setPic] = useState("");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try { const { data } = await api.get("/production/schedule"); setData(data); } catch (e) { setData({ rows: [], summary: {}, pics: [] }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = data?.rows || [];
  const sum = data?.summary || {};
  const stages = useMemo(() => [...new Set(rows.map((r) => r.stage))], [rows]);

  const filtered = rows.filter((r) => {
    if (filter === "GREEN" && r.prod_status !== "GREEN") return false;
    if (filter === "YELLOW" && r.prod_status !== "YELLOW") return false;
    if (filter === "RED" && r.prod_status !== "RED") return false;
    if (filter === "READY" && r.prod_status !== "READY") return false;
    if (filter === "TODAY" && (r.target_finish_date || "").slice(0, 10) !== today()) return false;
    if (stage && r.stage !== stage) return false;
    if (pic && r.pic !== pic) return false;
    return true;
  });

  const setEdit = (id, k, v) => setEdits((s) => ({ ...s, [id]: { ...s[id], [k]: v } }));
  const rowVal = (r, k) => (edits[r.id]?.[k] !== undefined ? edits[r.id][k] : (r[k] || "").slice ? (r[k] || "").slice(0, k.includes("date") ? 10 : undefined) : r[k] || "");
  const dirty = (id) => !!edits[id];

  const save = async (r) => {
    const e = edits[r.id] || {};
    try {
      await api.put(`/claims/${r.id}`, { pic: e.pic ?? r.pic, target_finish_date: e.target_finish_date ?? r.target_finish_date, target_unit_out_date: e.target_unit_out_date ?? r.target_unit_out_date });
      toast.success("Jadwal produksi disimpan");
      setEdits((s) => { const n = { ...s }; delete n[r.id]; return n; });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Gagal menyimpan"); }
  };

  const chips = [["ALL", `Semua (${rows.length})`], ["GREEN", `Green (${sum.green || 0})`], ["YELLOW", `Yellow (${sum.yellow || 0})`], ["RED", `Overdue (${sum.red || 0})`], ["TODAY", "Finish Today"], ["READY", `Ready (${sum.ready_delivery || 0})`]];

  return (
    <Shell>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] ti-gold"><CalendarClock size={13} /> Titanium Control</div>
      <h1 className="mt-1 font-heading text-2xl font-extrabold text-white sm:text-3xl">Production Schedule</h1>
      <p className="mt-1 text-sm ti-muted">Control center semua Repair Order aktif · sumber data tunggal (WO existing)</p>

      <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <SummaryTile label="In Workshop" value={sum.in_workshop} onClick={() => setFilter("ALL")} active={filter === "ALL"} />
        <SummaryTile label="On Track" value={sum.green} tone="text-emerald-300" onClick={() => setFilter("GREEN")} active={filter === "GREEN"} />
        <SummaryTile label="Due Soon" value={sum.yellow} tone="text-amber-300" onClick={() => setFilter("YELLOW")} active={filter === "YELLOW"} />
        <SummaryTile label="Overdue" value={sum.red} tone="text-red-300" onClick={() => setFilter("RED")} active={filter === "RED"} />
        <SummaryTile label="Ready Delivery" value={sum.ready_delivery} tone="text-sky-300" onClick={() => setFilter("READY")} active={filter === "READY"} />
        <SummaryTile label="Avg Aging" value={`${sum.avg_aging || 0}h`} />
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-2">
        {chips.map(([k, l]) => (
          <button key={k} data-testid={`prod-filter-${k.toLowerCase()}`} onClick={() => setFilter(k)} className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${filter === k ? "ti-gold-bg text-[#0A101F]" : "border ti-hair bg-[#0E1729] text-[#C7D2E5]"}`}>{l}</button>
        ))}
        <select data-testid="prod-filter-stage" value={stage} onChange={(e) => setStage(e.target.value)} className="rounded-full border ti-hair bg-[#0E1729] px-3 py-1.5 text-xs text-[#C7D2E5] outline-none">
          <option value="">By Stage</option>
          {stages.map((s) => <option key={s} value={s}>{label(s)}</option>)}
        </select>
        <select data-testid="prod-filter-pic" value={pic} onChange={(e) => setPic(e.target.value)} className="rounded-full border ti-hair bg-[#0E1729] px-3 py-1.5 text-xs text-[#C7D2E5] outline-none">
          <option value="">By PIC</option>
          {(data?.pics || []).map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>

      {filtered.length === 0 && (
        <div data-testid="prod-empty" className="ti-card mt-6 flex flex-col items-center gap-2 py-16 text-center">
          <Wrench size={32} className="ti-muted" />
          <p className="font-heading text-base font-semibold text-white">Tidak Ada Unit</p>
          <p className="text-sm ti-muted">Tidak ada unit yang cocok dengan filter ini.</p>
        </div>
      )}

      <div className="mt-5 space-y-3">
        {filtered.map((r) => {
          const m = META[r.prod_status] || META.NO_TARGET;
          return (
            <div key={r.id} data-testid={`prod-row-${r.id}`} className="ti-card p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <button onClick={() => navigate(`/claims/${r.id}`)} className="flex items-center gap-3 text-left">
                  <span className="ti-plate text-sm">{r.license_plate}</span>
                  <div>
                    <div className="text-sm font-semibold text-white">{r.vehicle || "—"} <span className="ti-muted">· {r.customer_name || "—"}</span></div>
                    <div className="text-xs ti-muted">{r.wo_number} · {r.claim_no} · {r.insurance_company}</div>
                  </div>
                </button>
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${m.b}`}>● {m.lbl}</span>
                  <ChevronRight size={16} className="ti-muted" onClick={() => navigate(`/claims/${r.id}`)} />
                </div>
              </div>

              <div className="mt-3 flex items-center gap-3">
                <span className={`flex items-center gap-1.5 text-xs font-semibold ${m.t}`}><span className={`h-2 w-2 rounded-full ${m.dot}`} />{label(r.stage)}</span>
                <div className="h-2 flex-1 overflow-hidden rounded-full ti-progress"><span className="block h-full rounded-full" style={{ width: `${r.progress}%` }} /></div>
                <span className="text-xs ti-muted">{r.progress}% · aging {r.aging_days}h</span>
              </div>

              <div className="mt-3 grid grid-cols-1 gap-2 border-t ti-hair pt-3 sm:grid-cols-4 sm:items-end">
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-wider ti-muted">PIC</div>
                  <input data-testid={`prod-pic-${r.id}`} value={rowVal(r, "pic")} onChange={(e) => setEdit(r.id, "pic", e.target.value)} placeholder="Nama teknisi" className="mt-1 h-8 w-full rounded border ti-hair bg-[#0E1729] px-2 text-xs text-white outline-none focus:border-[#3B82F6]/60" />
                </div>
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-wider ti-muted">Target Finish</div>
                  <input data-testid={`prod-finish-${r.id}`} type="date" value={rowVal(r, "target_finish_date")} onChange={(e) => setEdit(r.id, "target_finish_date", e.target.value)} className="mt-1 h-8 w-full rounded border ti-hair bg-[#0E1729] px-2 text-xs text-white outline-none focus:border-[#3B82F6]/60" />
                </div>
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-wider ti-muted">Target Unit Out</div>
                  <input data-testid={`prod-out-${r.id}`} type="date" value={rowVal(r, "target_unit_out_date")} onChange={(e) => setEdit(r.id, "target_unit_out_date", e.target.value)} className="mt-1 h-8 w-full rounded border ti-hair bg-[#0E1729] px-2 text-xs text-white outline-none focus:border-[#3B82F6]/60" />
                </div>
                <button data-testid={`prod-save-${r.id}`} disabled={!dirty(r.id)} onClick={() => save(r)} className={`flex h-8 items-center justify-center gap-1.5 rounded text-xs font-bold ${dirty(r.id) ? "ti-gold-bg text-[#0A101F]" : "cursor-not-allowed bg-white/5 text-[#5B6B87]"}`}><Save size={13} /> Simpan</button>
              </div>
            </div>
          );
        })}
      </div>
    </Shell>
  );
}
